import theme from '@/assets/theme';
import { CustomShipmentText, TeamBadge, ToggleSwitch } from '@/components';
import { themeT } from '@/utils/themeText';
import { Flex, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import '../logiblockDashboard.scss';
import { CopyContent, RightArrowIcon } from '@/assets/icons/icons';
import { Workflow } from '../LogiblockDashboard';

interface LogiblocksDashboardTableProps {
  workflowList: Workflow[];
  onWorkflowClick: (workflowId: string) => void;
  onToggleLiveness: (workflowId: string, currentlyDraft: boolean) => void;
  /** Called when the user clicks a "Duplicate workflow" button. */
  onDuplicateWorkflow: (workflowId: string) => void;
  /** Workflows whose duplicate is still in progress; their button is disabled. */
  duplicatingWorkflowIds: Set<string>;
}

const LogiblocksDashboardTable: React.FC<LogiblocksDashboardTableProps> = ({
  onWorkflowClick,
  workflowList,
  onToggleLiveness,
  onDuplicateWorkflow,
  duplicatingWorkflowIds,
}) => {
  const columns: ColumnsType<Workflow> = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (text: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Typography.Text
            ellipsis={{ tooltip: text }}
            style={{ width: '100%' }}
          >
            {text}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Last run',
      dataIndex: 'last_run',
      width: 100,
      render: (text: number) => {
        const isSuccess = text === 1;
        return (
          <>
            {text ? (
              <div
                className='lb-badge'
                style={{
                  background: isSuccess
                    ? 'var(--thin-green)'
                    : 'var(--light-gray)',
                  whiteSpace: 'nowrap',
                }}
              >
                <CustomShipmentText
                  text={isSuccess ? 'Success' : 'Fail'}
                  fontSize='10px'
                  noText='--'
                  color={
                    isSuccess ? 'var(--green-900)' : 'var(--text-secondary)'
                  }
                  fontWeight={600}
                />
              </div>
            ) : (
              '--'
            )}
          </>
        );
      },
    },

    {
      title: 'Runs',
      dataIndex: 'runs',
      render: (text: number) => (
        <CustomShipmentText text={String(text)} fontSize='10px' noText='--' />
      ),
    },

    {
      title: 'Success rate',
      dataIndex: 'success_rate',
      render: (text: string) => (
        <CustomShipmentText text={text} fontSize='10px' noText='--' />
      ),
    },

    {
      title: 'Failures',
      dataIndex: 'failures',
      render: (text: number) => (
        <CustomShipmentText text={String(text)} fontSize='10px' noText='--' />
      ),
    },

    {
      title: 'Run time (avg.)',
      dataIndex: 'avg_run_time',
      render: (text: string) => (
        <CustomShipmentText text={text} fontSize='10px' noText='--' />
      ),
    },

    {
      title: 'Last updated',
      dataIndex: 'last_updated',
      render: (text: string) => (
        <CustomShipmentText text={text} fontSize='10px' noText='--' />
      ),
    },
    {
      title: 'Created on',
      dataIndex: 'created_on',
      render: (text: string) => (
        <CustomShipmentText
          text={text ? text?.split(' ')[0] : text}
          fontSize='10px'
          noText='--'
        />
      ),
    },

    {
      title: 'Created by',
      dataIndex: 'created_by',
      render: (text: string) => (
        <CustomShipmentText text={text} fontSize='10px' noText='--' />
      ),
    },

    {
      title: `${themeT('team', theme.themeType)}`,
      dataIndex: 'teams',
      render: (_: unknown, record: Workflow) => {
        if (!record?.teams?.length) return <div>--</div>;

        return (
          <div className='flex gap-x-2 white-space-nowrap'>
            <TeamBadge
              color={record.teams[0]?.color}
              text={record.teams[0]?.name}
              tooltipText={record.teams[0]?.name}
            />

            {record.teams.length > 1 && (
              <Tooltip
                title={record.teams
                  .slice(1)
                  .map((team) => team.name)
                  .join(', ')}
              >
                <div
                  style={{ display: 'inline-block', cursor: 'pointer' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <TeamBadge
                    color={null}
                    text={`+ ${record.teams.length - 1}`}
                  />
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },

    {
      title: '',
      dataIndex: 'is_draft',
      key: 'is_draft',
      render: (_: unknown, record: Workflow) => (
        <Flex onClick={(e) => e.stopPropagation()}>
          <ToggleSwitch
            checked={!record.is_draft}
            onChange={() => onToggleLiveness(record.id, record.is_draft)}
          />
        </Flex>
      ),
    },
    {
      title: '',
      dataIndex: 'duplicate',
      key: 'duplicate',
      width: 50,
      render: (_: unknown, record: Workflow) => {
        const isDuplicating = duplicatingWorkflowIds.has(record.id);
        return (
          <Tooltip title='Duplicate workflow'>
            <Flex
              role='button'
              aria-label='Duplicate workflow'
              style={{
                cursor: isDuplicating ? 'default' : 'pointer',
                opacity: isDuplicating ? 0.5 : 1,
                pointerEvents: isDuplicating ? 'none' : 'auto',
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDuplicateWorkflow(record.id);
              }}
            >
              <CopyContent size={30} />
            </Flex>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'action',
      render: () => (
        <Flex justify='flex-end' align='center'>
          <div className='agents-openAgentBtn'>
            <RightArrowIcon width={16} height={16} />
          </div>
        </Flex>
      ),
    },
  ];
  return (
    <>
      <Table
        columns={columns}
        dataSource={workflowList}
        rowKey={(record) => record.id}
        pagination={false}
        className='settings-table lb-table'
        rowHoverable={false}
        onRow={(record) => ({
          onClick: () => onWorkflowClick(record.id),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 'max-content' }}
      />
    </>
  );
};

export default LogiblocksDashboardTable;
